
























































import Vue from 'vue';
import InfiniteLoading from 'vue-infinite-loading';
import { debounce, get, escapeRegExp } from 'lodash';

import { BFormCheckbox } from 'bootstrap-vue';
import ZenButton from '@reciprocity/button';
import SearchableZeroState from '@/components/object_mapper/SearchableZeroState.vue';
import { MappedItem } from './mapper_types';

export default Vue.extend({
  name: 'SearchableList',
  components: {
    InfiniteLoading,
    BFormCheckbox,
    ZenButton,
    SearchableZeroState,
  },
  props: {
    data: {
      type: Array as () => MappedItem[],
      required: true,
    },
    selectedItem: {
      type: Object,
      default: () => {},
    },
    actionBtnCopy: {
      type: String,
      default: '',
    },
    showUntilFirstSelected: {
      type: Boolean,
      default: false,
    },
    searchProps: {
      type: Array as () => String[],
      default: () => [''],
    },
    objectType: {
      type: String,
      default: '',
    },
    mappedObjectsColumn: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchString: <string>'',
      // Push first 100 items on component load and skip infiniteHandler
      currentItems: this.data.slice(0, 100) as MappedItem[],
      infiniteLoadingKey: 1,
      lastSelectedItem: <MappedItem | null>null,
      allSelected: false,
      indeterminate: false,
      itemsLoaded: false,
    };
  },
  computed: {
    itemList(): MappedItem[] {
      if (this.searchString.length === 0) {
        return this.data;
      }

      return this.data.filter((item: MappedItem) => {
        // Concat all search property values into a big string
        const text = this.searchProps.reduce((acc: string, prop: string) => acc + get(item, prop, ''), '');
        try {
          return RegExp(this.searchString, 'i').test(text);
        } catch (err) {
          // if the search string is not a valid regular expression, try to match the escaped string
          const escapedString = escapeRegExp(this.searchString);
          return RegExp(escapedString, 'i').test(text);
        }
      });
    },
    selectedItems(): MappedItem[] {
      return this.itemList.filter(item => item.selected);
    },
    showWarning(): boolean {
      return this.itemList.length ? !this.itemList.some(item => item === this.selectedItem) : false;
    },
  },
  watch: {
    itemList() {
      let itemsAmountToLoad = 100;
      if (this.showUntilFirstSelected) {
        const firstSelectedItem = this.itemList.findIndex(i => i.selected);
        itemsAmountToLoad = firstSelectedItem > 85 ? firstSelectedItem + 15 : itemsAmountToLoad;
      }
      this.currentItems = this.itemList.slice(0, itemsAmountToLoad);

      // We increase the key to reset infinite loading component state.
      // Otherwise calling complete breaks the state of infinite loading
      // component.
      this.infiniteLoadingKey += 1;
    },
    async selectedItems(newVal) {
      await this.$nextTick();
      if (newVal.length === 0) {
        this.indeterminate = false;
        this.allSelected = false;
      } else if (newVal.length === this.itemList.length) {
        this.indeterminate = false;
        this.allSelected = true;
      } else {
        this.indeterminate = true;
        this.allSelected = false;
      }
    },
  },
  methods: {
    clearSearch(): void {
      this.searchString = '';
    },
    debounceSearch(event: { target: HTMLInputElement }) {
      const val = event.target.value;
      searchFunction.call(this, val);
    },
    infiniteHandler($state: any) {
      const currentLen = this.currentItems.length;

      this.itemList.slice(currentLen, currentLen + 100).forEach(item => {
        this.currentItems.push(item);
      });

      $state.loaded();
      this.itemsLoaded = true;

      if (this.currentItems.length === this.itemList.length) {
        $state.complete();
      }
    },
    selectItems(item: MappedItem, ev: MouseEvent) {
      const prev = this.lastSelectedItem;
      if (ev.shiftKey && prev) {
        const start = this.itemList.indexOf(prev);
        const end = this.itemList.indexOf(item);

        this.itemList
          .slice(Math.min(start, end), Math.max(start, end) + 1)
          .forEach(elem => (elem.selected = item.selected));
      }
      this.lastSelectedItem = item;
    },
    triggerAction() {
      this.$emit('action', this.selectedItems);
    },
    selectAll(selected: boolean) {
      if (this.indeterminate) {
        this.itemList.forEach(item => (item.selected = false));
      } else {
        this.itemList.forEach(item => (item.selected = selected));
      }
    },
    scrollToFirstSelectedItem() {
      const itemEl = this.$el.querySelector('.selected');
      if (!itemEl) return;
      itemEl.scrollIntoView({ behavior: 'auto', block: 'start' });
      this.cropSelectedItem(itemEl);
    },
    cropSelectedItem(itemEl) {
      const isFirst = !itemEl.previousElementSibling;
      const isDisplayedCropped = itemEl.scrollTop !== 0;
      if (isFirst || isDisplayedCropped) return;
      const offset = itemEl.previousElementSibling.scrollHeight * (2 / 3);
      this.$el.querySelector('.list-block').scrollBy({ top: -offset, behavior: 'auto' });
    },
  },
});

const searchFunction = debounce(function(this: { searchString: string }, val: string) {
  this.searchString = val;
}, 150);
